import { useState } from "react";
import { toast } from "sonner";
import { Circle, Marker, Popup } from "react-leaflet";
import { Map } from "@/components/ui/map";
import icon from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";
import iconRetina from "leaflet/dist/images/marker-icon-2x.png";
import L, { LatLngTuple } from "leaflet";
import { Slider } from "@/components/ui/slider";
import "leaflet/dist/leaflet.css";
import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import { Button } from "@/components/catalyst/button";
import { useGqlMutation, useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { keepPreviousData } from "@tanstack/react-query";
import { createWorkOrderJobOpeningOffersMutation } from "@/app/ProDrivers/WorkOrders/GraphQL/jobOpeningOfferMutations";
import { ProDriverTable } from "@/app/ProDrivers/Drivers/Components/ProDriverTable";
import { proDriverCountQuery } from "@/app/ProDrivers/Drivers/GraphQL/proDriverCountQuery";
import { useTranslations } from "use-intl";
import { useWorkOrderRecord } from "@/app/ProDrivers/WorkOrders/Hooks/useWorkOrderRecord";
import { BasemapLayer } from "@/components/map/BasemapLayer";

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconRetinaUrl: iconRetina,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

L.Marker.prototype.options.icon = DefaultIcon;

interface SendToDriversDialogProps {
  jobOpeningId: string;
}

export function SendToDriversDialog({
  jobOpeningId,
}: SendToDriversDialogProps) {
  const t = useTranslations("workOrder.jobOpenings");
  const tCreate = useTranslations("workOrder.jobOpenings.createDialog");
  const tMap = useTranslations("workOrder.driversScreen");
  const tDetails = useTranslations("workOrder.details");
  const tTable = useTranslations("proDriver.table");
  const { close } = useDialogState();
  const { departureAddress, deliveryAddress } = useWorkOrderRecord();
  const [locationMode, setLocationMode] = useState<"departure" | "delivery">(
    "departure",
  );
  const address =
    locationMode === "departure" ? departureAddress : deliveryAddress;
  const center = [address.lat, address.lng] as LatLngTuple;
  const [radius, setRadius] = useState(100);
  const [mode, setMode] = useState<"all" | "specific">("all");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const radiusAround = { radius, lat: center[0], lng: center[1] };
  const { data: countData } = useGqlQuery(
    proDriverCountQuery,
    {
      radiusAround,
      excludeJobOpeningId: jobOpeningId,
      archived: false,
    },
    { placeholderData: keepPreviousData },
  );
  const driversInArea = countData?.proDrivers.paginatorInfo.total ?? 0;

  const { mutateAsync: createOffers, isPending } = useGqlMutation(
    createWorkOrderJobOpeningOffersMutation,
  );

  const handleSubmit = async () => {
    await createOffers({
      input: {
        job_opening_id: jobOpeningId,
        ...(mode === "all"
          ? {
              driver_filter: {
                radiusAround,
                excludeJobOpeningId: jobOpeningId,
                archived: false,
              },
            }
          : { pro_driver_ids: selectedIds }),
      },
    });
    toast.success(t("sendToDrivers.success"));
    close();
  };

  const submitDisabled =
    isPending ||
    (mode === "all" ? driversInArea === 0 : selectedIds.length === 0);

  return (
    <BaseDialog
      title={t("sendToDrivers.title")}
      subtitle={t("sendToDrivers.subtitle")}
      size="5xl"
    >
      <div className="space-y-4">
        <div className="relative overflow-hidden rounded-lg border shadow-lg">
          <Map
            key={locationMode}
            center={center}
            zoom={5}
            scrollWheelZoom={false}
            height="300px"
          >
            <BasemapLayer />
            <Marker position={center}>
              <Popup>
                <div className="text-center">
                  <strong>{tMap("searchCenter")}</strong>
                  <br />
                  {tMap("lookingForDrivers", { radius })}
                </div>
              </Popup>
            </Marker>
            <Circle
              center={center}
              radius={radius * 1000}
              pathOptions={{
                fillColor: "#3b82f6",
                fillOpacity: 0.1,
                color: "#3b82f6",
                weight: 2,
                opacity: 0.6,
              }}
            />
          </Map>

          <div className="absolute right-4 top-4 z-[1000] w-64 rounded-lg border bg-white/95 p-4 shadow-lg backdrop-blur-sm">
            <div className="space-y-3">
              <div className="flex rounded-md bg-gray-100 p-0.5">
                <button
                  type="button"
                  onClick={() => setLocationMode("departure")}
                  className={`flex-1 whitespace-nowrap rounded px-2 py-1 text-xs font-medium transition-colors ${
                    locationMode === "departure"
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {tDetails("pickUpLocation")}
                </button>
                <button
                  type="button"
                  onClick={() => setLocationMode("delivery")}
                  className={`flex-1 whitespace-nowrap rounded px-2 py-1 text-xs font-medium transition-colors ${
                    locationMode === "delivery"
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {tDetails("dropOffLocation")}
                </button>
              </div>
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">
                  {tMap("searchRadius")}
                </label>
                <span className="text-sm text-gray-500">{radius} km</span>
              </div>
              <Slider
                value={[radius]}
                onValueChange={(value) => setRadius(value[0])}
                max={500}
                min={50}
                step={25}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-400">
                <span>50km</span>
                <span>500km</span>
              </div>

              <div className="flex rounded-md bg-gray-100 p-0.5">
                <button
                  type="button"
                  onClick={() => {
                    setMode("all");
                    setSelectedIds([]);
                  }}
                  className={`flex-1 whitespace-nowrap rounded px-2 py-1 text-xs font-medium transition-colors ${
                    mode === "all"
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {tCreate("inviteAllDrivers", { count: driversInArea })}
                </button>
                <button
                  type="button"
                  onClick={() => setMode("specific")}
                  className={`flex-1 whitespace-nowrap rounded px-2 py-1 text-xs font-medium transition-colors ${
                    mode === "specific"
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {tCreate("selectSpecificDrivers")}
                </button>
              </div>
            </div>
          </div>
        </div>

        {mode === "specific" ? (
          <ProDriverTable
            id="send-to-drivers-dialog"
            showActions={false}
            queryVariables={{
              excludeJobOpeningId: jobOpeningId,
              radiusAround,
            }}
            selection={{
              selectedIds,
              onChange: setSelectedIds,
            }}
          />
        ) : null}

        <div className="flex items-center justify-end gap-3">
          {mode === "specific" && selectedIds.length > 0 ? (
            <span className="text-xs text-gray-500">
              {selectedIds.length} {tTable("selected")}
            </span>
          ) : null}
          <Button
            type="button"
            color="blue"
            onClick={handleSubmit}
            disabled={submitDisabled}
            isLoading={isPending}
          >
            {mode === "all"
              ? tCreate("inviteAllDrivers", { count: driversInArea })
              : t("sendToDrivers.send")}
          </Button>
        </div>
      </div>
    </BaseDialog>
  );
}
